import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiTrendingUp } = FiIcons;

const ResultsSection = () => {
  const stats = [
    {
      number: "28%",
      label: "increase in tactical decision speed",
      color: "accent-magenta"
    },
    {
      number: "15%",
      label: "reduction in motor errors under pressure",
      color: "accent-gold"
    },
    {
      number: "2.5x",
      label: "higher likelihood of achieving elite status",
      color: "accent-purple"
    },
    {
      number: "20-30%",
      label: "boost in overall executive function performance",
      color: "accent-lavender"
    },
    {
      number: "92%",
      label: "report improved focus during competition",
      color: "accent-yellow"
    }
  ];

  const testimonials = [
    {
      quote: "The mental clarity is insane—I see plays developing before they happen.",
      author: "M.C"
    },
    {
      quote: "My coach says I'm a different player. Same skills, but now my brain runs the show now.",
      author: "T.D."
    }
  ];

  return (
    <section id="results" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            The Numbers That Separate Champions from Competitors
          </h2>
          <p className="text-xl text-gray-700 mb-8">Our Athletes See:</p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className={`bg-gradient-to-br from-${stat.color}/10 to-${stat.color}/5 p-8 rounded-2xl border border-${stat.color}/20 text-center hover:shadow-lg transition-all duration-300`}
            >
              <div className={`text-4xl lg:text-5xl font-bold text-${stat.color} mb-4`}>
                {stat.number}
              </div>
              <p className="text-gray-700 font-medium leading-relaxed">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-gray-900 text-center mb-12">
            Real Results:
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 relative"
              >
                <div className="absolute -top-4 left-8">
                  <div className="bg-accent-magenta text-white p-3 rounded-full">
                    <SafeIcon icon={FiStar} className="h-6 w-6" />
                  </div>
                </div>
                <blockquote className="text-lg text-gray-800 italic leading-relaxed mb-6 pt-4">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center justify-between">
                  <cite className="text-accent-magenta font-semibold not-italic">
                    — {testimonial.author}
                  </cite>
                  <div className="flex text-accent-gold">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-xl">★</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-accent-light/50 to-accent-purple/30 p-8 lg:p-12 rounded-2xl border border-accent-lavender/30">
            <p className="text-xl text-gray-800 mb-8 leading-relaxed max-w-3xl mx-auto">
              These results aren't accidents. They're the predictable outcome of training your brain with the same precision you train your body.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-primary text-lg"
              >
                Get Your Results
              </button>
              <a
                href="https://www.wethebillions.com/quiz"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary text-lg"
              >
                Take Free Assessment
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ResultsSection;